import React, { useState, useEffect } from 'react';
import { Editor, EditorState, ContentState, CompositeDecorator } from 'draft-js';
import 'draft-js/dist/Draft.css';
import { toast } from 'react-hot-toast';
import { DialogFlowPayloadManager } from '../utils/dialogFlowPayloadManager';
import api from '../utils/api';
import AppLoadingState from './common/AppLoadingState';

// Add Variable Decorator Component
const VariableSpan = (props) => {
  return (
    <span
      style={{
        backgroundColor: '#E5EDFF',
        padding: '2px 4px',
        borderRadius: '4px',
        color: '#4F46E5',
        fontFamily: 'monospace',
        margin: '0 2px',
        border: '1px solid #C7D2FE'
      }}
      data-offset-key={props.offsetKey}
    >
      {props.children}
    </span>
  );
};

// Variable matching strategy
const findVariableEntities = (contentBlock, callback, contentState) => {
  const text = contentBlock.getText();
  const regex = /\{\{[^}]+\}\}/g;
  let matchArr;
  while ((matchArr = regex.exec(text)) !== null) {
    const start = matchArr.index;
    const end = start + matchArr[0].length;
    callback(start, end);
  }
};

// Create decorator
const decorator = new CompositeDecorator([
  {
    strategy: findVariableEntities,
    component: VariableSpan,
  },
]);

const NodeModal = ({ node, onClose, onSave, onDelete, onDuplicate }) => {
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // Use NodePayloadManager for node types
  const nodeTypes = DialogFlowPayloadManager.getNodeTypes();

  const modalSectionClass =
    'rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)] p-5';
  const fieldClass =
    'w-full rounded-xl border border-[var(--app-field-border)] bg-[var(--app-field-bg)] p-2.5 text-[var(--app-ink)] focus:border-[var(--app-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--app-accent-ring)]';

  const [formData, setFormData] = useState({
    name: '',
    text: '',
    prompt: '',
    type: 'Default',
    isStaticText: !!node?.data?.text && !node?.data?.prompt,
    loopConditionEnabled: false,
    isGlobal: false,
    extractVariables: false,
    tagName: '',
    tagColor: '#000000',
    condition: '',
    globalPrompt: '',
    globalLabel: '',
    extractVars: [],
    conditionExamples: [],
    kbTool: '',
    modelOptions: {
      modelType: 'smart',
      temperature: 0.2,
      isSMSReturnNode: false,
      skipUserResponse: false,
      block_interruptions: false,
      disableSilenceRepeat: false
    }
  });

  const [editorState, setEditorState] = useState(() => {
    const initialText = node?.data?.text || node?.data?.prompt || '';
    return EditorState.createWithContent(
      ContentState.createFromText(initialText),
      decorator
    );
  });

  // Add useEffect to fetch knowledge bases when type is Vector DB Knowledge Base
  useEffect(() => {
    if (formData.type === 'Vector DB Knowledge Base') {
      const fetchKnowledgeBases = async () => {
        setIsLoading(true);
        try {
          const response = await api.getKnowledgeBases();
          setKnowledgeBases(response.data.vectors || []);
          setError(null);
        } catch (error) {
          console.error('Error fetching knowledge bases:', error);
          setError('Failed to fetch knowledge bases. Please try again.');
        } finally {
          setIsLoading(false);
        }
      };
      fetchKnowledgeBases();
    }
  }, [formData.type]);

  // Initialize form data from node
  useEffect(() => {
    if (node?.data) {
      // Check for condition and loopConditionEnabled independently
      const hasCondition = typeof node.data.condition === 'string' && node.data.condition.length > 0;
      const wasEnabled = !!node.data.loopConditionEnabled;
      // The node should show as having loop condition if it was previously enabled OR if it has a condition
      const shouldEnableLoop = wasEnabled || hasCondition;

      setFormData({
        name: node.data.name || '',
        text: node.data.text || '',
        prompt: node.data.prompt || '',
        type: node.data.type || 'Default',
        isStaticText: !!node.data.text && !node.data.prompt,
        // Enable loop condition if it was enabled before or has a condition
        loopConditionEnabled: shouldEnableLoop,
        // Always preserve the existing condition if it exists
        condition: node.data.condition || '',
        // Handle condition examples separately
        conditionExamples: Array.isArray(node.data.conditionExamples) ? 
          node.data.conditionExamples : [],
        isGlobal: !!node.data.isGlobal,
        globalLabel: node.data.globalLabel || '',
        extractVariables: !!node.data.extractVars?.length,
        tagName: node.data.tag?.name || '',
        tagColor: node.data.tag?.color || '#000000',
        globalPrompt: node.data.globalPrompt || '',
        extractVars: node.data.extractVars || [],
        kbTool: node.data.kbTool || '',
        modelOptions: node.data.modelOptions || {
          modelType: 'smart',
          temperature: 0.2,
          isSMSReturnNode: false,
          skipUserResponse: false,
          block_interruptions: false,
          disableSilenceRepeat: false
        }
      });

      // Initialize editor with existing content
      const content = node.data.text || node.data.prompt || '';
      setEditorState(
        EditorState.createWithContent(
          ContentState.createFromText(content),
          decorator
        )
      );
    }
  }, [node]);

  // Handle node type change
  const handleNodeTypeChange = (newType) => {
    const newPayload = DialogFlowPayloadManager.createNodePayload({
      id: node.id,
      type: newType,
      position: node.position,
      data: {
        ...formData,
        type: newType
      },
      style: node.style
    });

    setFormData(newPayload.data);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Get current content from editor
    const currentContent = editorState.getCurrentContent();
    const content = currentContent.getPlainText();

    // Create the node data, preserving existing data and overriding with new values
    const nodeData = {
      ...node.data,  // Preserve all existing data
      name: formData.name,
      text: formData.isStaticText ? content : '',
      prompt: !formData.isStaticText ? content : '',
      type: formData.type,
      isGlobal: formData.isGlobal,
      globalLabel: formData.globalLabel,
      condition: formData.loopConditionEnabled ? formData.condition : undefined,
      conditionExamples: formData.loopConditionEnabled ? formData.conditionExamples : undefined,
      loopConditionEnabled: formData.loopConditionEnabled,
      tag: formData.tagName ? {
        name: formData.tagName,
        color: formData.tagColor
      } : null,
      globalPrompt: formData.globalPrompt,
      extractVars: formData.extractVariables ? formData.extractVars : [],
      modelOptions: formData.modelOptions
    };

    // Handle type-specific fields
    if (formData.type === 'Vector DB Knowledge Base') {
      nodeData.kbTool = formData.kbTool;
    } else if (formData.type === 'Transfer Call') {
      nodeData.transferNumber = formData.transferNumber;
    }

    // Validate the node data
    try {
      // Create a node payload for validation
      const nodePayload = DialogFlowPayloadManager.createNodePayload({
        id: node.id,
        type: formData.type,
        data: nodeData
      });

      // Validate the payload
      if (DialogFlowPayloadManager.validateNodePayload(nodePayload)) {
        onSave({
          id: node.id,
          data: nodeData,
          type: formData.type
        });
        onClose();
      } else {
        console.error('Invalid node configuration');
        alert('Error: Invalid node configuration. Please check all fields.');
      }
    } catch (error) {
      console.error('Error validating node:', error);
      alert('Error validating node configuration. Please check all fields.');
    }
  };

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(node.id);
      toast.success('Node ID copied');
    } catch {
      toast.error('Could not copy ID');
    }
  };

  const Switch = ({ enabled, onChange, label }) => (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      className={`${
        enabled ? 'bg-[var(--app-accent)]' : 'bg-[color:rgba(148,163,184,0.35)]'
      } relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--app-accent)]`}
      onClick={() => onChange(!enabled)}
    >
      <span className="sr-only">{label}</span>
      <span
        className={`${
          enabled ? 'translate-x-6' : 'translate-x-1'
        } inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition duration-200 ease-out`}
      />
    </button>
  );

  const handleEditorChange = (newEditorState) => {
    setEditorState(newEditorState);
    const currentContent = newEditorState.getCurrentContent();
    const content = currentContent.getPlainText();
    setFormData(prev => ({
      ...prev,
      text: prev.isStaticText ? content : '',
      prompt: !prev.isStaticText ? content : ''
    }));
  };

  const handleConditionToggle = (enabled) => {
    setFormData(prev => ({ 
      ...prev, 
      loopConditionEnabled: enabled
    }));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(9,12,16,0.55)] p-4 backdrop-blur-[6px]"
      role="presentation"
    >
      <div
        className="app-page flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] shadow-[var(--app-popover-shadow)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="node-modal-title"
      >
        <div className="overflow-y-auto px-8 pb-8 pt-7 space-y-5">
        {/* Error message display */}
        {error && (
          <div className="relative rounded-xl border border-[var(--app-alert-error-border)] bg-[var(--app-alert-error-bg)] px-4 py-3 text-[var(--app-alert-error-fg)]" role="alert">
            <span className="block pr-8 sm:inline">{error}</span>
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-[var(--app-alert-error-fg)] opacity-80 hover:opacity-100"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
            >
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[var(--app-border)] pb-5">
          <div className="min-w-0">
            <h2 id="node-modal-title" className="text-lg font-semibold tracking-tight text-[var(--app-ink)]">
              Edit Node
            </h2>
            <p className="mt-1 text-sm text-[var(--app-muted)]">
              Configure behavior, prompts, and model options for this step.
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={copyId}
              title="Copy node ID to clipboard"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--app-control-border)] bg-[var(--app-control-bg)] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[var(--app-control-fg)] transition-colors hover:bg-[var(--app-control-bg-hover)]"
            >
              <svg className="h-4 w-4 shrink-0 opacity-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
              </svg>
              Copy ID
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-[var(--app-muted)] transition-colors hover:bg-[var(--app-control-bg)] hover:text-[var(--app-ink)]"
              title="Close"
              aria-label="Close"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Node Type */}
        <div>
          <label htmlFor="nodeType" className="mb-2 block text-sm font-medium text-[var(--app-muted)]">Node Type</label>
          <select
            id="nodeType"
            className="block w-full rounded-xl border border-[var(--app-field-border)] bg-[var(--app-field-bg)] p-2.5 text-[var(--app-ink)] focus:border-[var(--app-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--app-accent-ring)]"
            value={formData.type}
            onChange={(e) => handleNodeTypeChange(e.target.value)}
          >
            {nodeTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.value}
              </option>
            ))}
          </select>
        </div>

        {/* Knowledge Base Selector - Only show for Vector DB Knowledge Base type */}
        {formData.type === 'Vector DB Knowledge Base' && (
          <div className={modalSectionClass}>
            <h3 className="mb-1 text-base font-semibold text-[var(--app-ink)]">Knowledge Base Settings</h3>
            <p className="mb-4 text-sm text-[var(--app-muted)]">Choose which knowledge base this node queries.</p>
            <div>
              <label className="mb-2 block text-sm font-medium text-[var(--app-muted)]">Select knowledge base</label>
              <select
                value={formData.kbTool}
                onChange={(e) => setFormData({
                  ...formData,
                  kbTool: e.target.value
                })}
                className={fieldClass}
                disabled={isLoading}
              >
                <option value="">Select a knowledge base...</option>
                {knowledgeBases.map((kb) => (
                  <option key={kb.vector_id} value={kb.vector_id}>
                    {kb.name}
                  </option>
                ))}
              </select>
              {isLoading ? (
                <div className="mt-2 flex justify-center">
                  <AppLoadingState
                    layout="inline"
                    label="Loading knowledge bases…"
                    className="text-sm text-[var(--app-muted)]"
                  />
                </div>
              ) : knowledgeBases.length === 0 && !error ? (
                <p className="mt-2 text-sm text-[var(--app-muted)]">
                  No knowledge bases found. Please create one first.
                </p>
              ) : null}
            </div>
          </div>
        )}

        {/* Name */}
        <div>
          <label className="mb-2 block text-sm font-medium text-[var(--app-muted)]">Name</label>
          <input
            type="text"
            className={fieldClass}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        </div>

        {/* Static Text Toggle */}
        <div className="flex items-center gap-4 rounded-xl border border-[var(--app-border)] bg-[var(--app-surface)] px-4 py-3">
          <Switch
            enabled={formData.isStaticText}
            onChange={(enabled) => setFormData({ ...formData, isStaticText: enabled })}
            label="Static Text"
          />
          <label className="text-sm text-[var(--app-ink)]">
            <span className="font-medium">Static text</span>{' '}
            <span className="text-[var(--app-muted)]">
              (fixed copy instead of AI-generated replies)
            </span>
          </label>
        </div>

        {/* Text Editor */}
        <div>
          <label className="mb-2 block text-sm font-medium text-[var(--app-muted)]">
            {formData.isStaticText ? 'Text' : 'Prompt'}
          </label>
          <div className="node-modal-draft relative min-h-[150px] w-full rounded-xl border border-[var(--app-field-border)] bg-[var(--app-field-bg)] p-4">
            <Editor
              editorState={editorState}
              onChange={handleEditorChange}
              placeholder="Enter node text here..."
            />
          </div>
        </div>

        {/* Loop Condition */}
        <div className={modalSectionClass}>
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-[var(--app-ink)]">Loop condition</h3>
              <p className="mt-1 text-sm text-[var(--app-muted)]">
                Keep the agent on this node until a specific condition is met.
              </p>
            </div>
            <Switch
              enabled={formData.loopConditionEnabled}
              onChange={handleConditionToggle}
              label="Enable Condition"
            />
          </div>

          {formData.loopConditionEnabled && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-[var(--app-muted)]">Condition</label>
                <textarea
                  value={formData.condition || ''}
                  onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                  className={`${fieldClass} min-h-[100px] resize-y py-3`}
                  placeholder="Example: User has confirmed their appointment time"
                />
              </div>
              
              <div>
                <label className="mb-2 block text-sm font-medium text-[var(--app-muted)]">Condition examples</label>
                <div className="space-y-2">
                  {(formData.conditionExamples || []).map((example, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={example["Conversation History"] || ""}
                        onChange={(e) => {
                          const newExamples = [...formData.conditionExamples];
                          newExamples[index] = {
                            ...newExamples[index],
                            "Conversation History": e.target.value
                          };
                          setFormData({ ...formData, conditionExamples: newExamples });
                        }}
                        className={`${fieldClass} flex-1`}
                        placeholder="Conversation History"
                      />
                      <input
                        type="text"
                        value={example["Condition Achieved"] || ""}
                        onChange={(e) => {
                          const newExamples = [...formData.conditionExamples];
                          newExamples[index] = {
                            ...newExamples[index],
                            "Condition Achieved": e.target.value,
                            "Conversation History": example["Conversation History"] || e.target.value
                          };
                          setFormData({ ...formData, conditionExamples: newExamples });
                        }}
                        className={`${fieldClass} flex-1`}
                        placeholder="Expected Condition Achieved"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newExamples = formData.conditionExamples.filter((_, i) => i !== index);
                          setFormData({ ...formData, conditionExamples: newExamples });
                        }}
                        className="rounded-lg p-2 text-[var(--app-danger)] hover:bg-[var(--app-danger-soft)]"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setFormData({
                      ...formData,
                      conditionExamples: [...(formData.conditionExamples || []), {
                        "Condition Achieved": "",
                        "Conversation History": ""
                      }]
                    })}
                    className="w-full rounded-xl border border-dashed border-[var(--app-field-border)] p-2.5 text-sm font-medium text-[var(--app-muted)] transition-colors hover:border-[var(--app-accent)] hover:bg-[var(--app-accent-soft)] hover:text-[var(--app-ink)]"
                  >
                    + Add example
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Global Node */}
        <div className={modalSectionClass}>
          <div className="flex items-center justify-between gap-4">
            <h3 className="text-base font-semibold text-[var(--app-ink)]">Global node</h3>
            <Switch
              enabled={formData.isGlobal}
              onChange={(enabled) => setFormData({ ...formData, isGlobal: enabled })}
              label="Global Node"
            />
          </div>
          <p className="mt-1 text-sm text-[var(--app-muted)]">
            Make this node reachable from anywhere in the flow.
          </p>
          {formData.isGlobal && (
            <div className="mt-4">
              <label className="mb-2 block text-sm font-medium text-[var(--app-muted)]">Condition to trigger this node</label>
              <input
                type="text"
                value={formData.globalLabel}
                onChange={(e) => setFormData({ ...formData, globalLabel: e.target.value })}
                className={fieldClass}
                placeholder="Example: User is asking specific company information"
              />
            </div>
          )}
        </div>

        {/* Model Options */}
        <div className={modalSectionClass}>
          <h3 className="mb-4 text-base font-semibold text-[var(--app-ink)]">Model options</h3>
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-[var(--app-muted)]">Model type</label>
              <select
                value={formData.modelOptions.modelType}
                onChange={(e) => setFormData({
                  ...formData,
                  modelOptions: { ...formData.modelOptions, modelType: e.target.value }
                })}
                className={fieldClass}
              >
                <option value="smart">Smart</option>
                <option value="fast">Fast</option>
              </select>
            </div>
            
            <div>
              <label className="mb-2 block text-sm font-medium text-[var(--app-muted)]">Temperature</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={formData.modelOptions.temperature}
                onChange={(e) => setFormData({
                  ...formData,
                  modelOptions: { ...formData.modelOptions, temperature: parseFloat(e.target.value) }
                })}
                className={fieldClass}
              />
            </div>

            <div className="flex items-center gap-4 rounded-lg border border-[var(--app-border)] bg-[var(--app-readonly-bg)] px-4 py-3">
              <Switch
                enabled={formData.modelOptions.skipUserResponse}
                onChange={(enabled) => setFormData({
                  ...formData,
                  modelOptions: { ...formData.modelOptions, skipUserResponse: enabled }
                })}
                label="Skip User Response"
              />
              <label className="text-sm text-[var(--app-ink)]">Skip user response</label>
            </div>

            <div className="flex items-center gap-4 rounded-lg border border-[var(--app-border)] bg-[var(--app-readonly-bg)] px-4 py-3">
              <Switch
                enabled={formData.modelOptions.block_interruptions}
                onChange={(enabled) => setFormData({
                  ...formData,
                  modelOptions: { ...formData.modelOptions, block_interruptions: enabled }
                })}
                label="Block Interruptions"
              />
              <label className="text-sm text-[var(--app-ink)]">Block interruptions</label>
            </div>
          </div>
        </div>

        {/* Transfer Number - Only show for Transfer Call nodes */}
        {formData.type === 'Transfer Call' && (
          <div className={modalSectionClass}>
            <h3 className="mb-4 text-base font-semibold text-[var(--app-ink)]">Transfer settings</h3>
            <div>
              <label className="mb-2 block text-sm font-medium text-[var(--app-muted)]">Transfer number</label>
              <input
                type="text"
                value={formData.transferNumber}
                onChange={(e) => setFormData({
                  ...formData,
                  transferNumber: e.target.value
                })}
                className={fieldClass}
                placeholder="Enter transfer number"
              />
            </div>
          </div>
        )}

        {/* Extract Variables */}
        {formData.extractVars?.length > 0 && (
          <div className={modalSectionClass}>
            <h3 className="mb-4 text-base font-semibold text-[var(--app-ink)]">Extract variables</h3>
            {formData.extractVars.map((variable, index) => (
              <div key={index} className="mb-4 rounded-xl border border-[var(--app-border)] bg-[var(--app-readonly-bg)] p-4">
                <input
                  value={variable[0]}
                  onChange={(e) => {
                    const newVars = [...formData.extractVars];
                    newVars[index][0] = e.target.value;
                    setFormData({ ...formData, extractVars: newVars });
                  }}
                  className={`${fieldClass} mb-2`}
                  placeholder="Variable name"
                />
                <textarea
                  value={variable[2]}
                  onChange={(e) => {
                    const newVars = [...formData.extractVars];
                    newVars[index][2] = e.target.value;
                    setFormData({ ...formData, extractVars: newVars });
                  }}
                  className={`${fieldClass} h-24 resize-y py-3`}
                  placeholder="Extraction instructions"
                />
              </div>
            ))}
            <button
              onClick={() => setFormData({
                ...formData,
                extractVars: [...formData.extractVars, ['', 'string', '', false]]
              })}
              className="rounded-xl border border-[var(--app-control-border)] bg-[var(--app-control-bg)] px-4 py-2 text-sm font-medium text-[var(--app-control-fg)] hover:bg-[var(--app-control-bg-hover)]"
              type="button"
            >
              Add variable
            </button>
          </div>
        )}

        {/* Save/Cancel buttons */}
        <div className="flex flex-row items-center justify-end gap-3 border-t border-[var(--app-border)] pt-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-[var(--app-control-border)] bg-transparent px-4 py-2.5 text-sm font-medium text-[var(--app-muted)] transition-colors hover:bg-[var(--app-control-bg)] hover:text-[var(--app-ink)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="rounded-xl bg-[var(--app-accent)] px-5 py-2.5 text-sm font-semibold text-[var(--app-accent-strong)] shadow-sm transition-colors hover:bg-[var(--app-accent-hover)]"
          >
            Save changes
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default NodeModal; 